import { useEffect, useState } from 'react'
import { Container, Col } from 'react-bootstrap'

import Loading from '../../../templates/Loading'
import PracticeIntroVideo from '../PracticeIntroVideo'
import PracticeCategoryFilter from '../PracticeCategoryFilter'
import ScrollingCardsPracticeList from '../../common/navigation/ScrollingCardsPracticeList'
import {
  getPracticeCategories,
  getPracticeDetails
} from '../../common/api/Practices'
import useFetchAllList from '../../common/hooks/useFetchAllList'
import ChatCTA from 'components/chat/ChatCTA/ChatCTA'
import { ROUTE_CHAT_GENERAL } from 'components/common/helpers/Paths'

// Helper function to get practice categories
async function _getPracticeCategoriesAsync(practiceKey) {
  let result = await getPracticeCategories(practiceKey)

  // Loop through all categories and create a new array that combines the parent and children
  // by adding the children to the parent object
  result = result.map(category => {
    // Initialize the subcategories array
    category.subcategories = category.subcategories || []

    // Because WordPress is stupid, sometimes the category is an object, sometimes it's an array
    if (!Array.isArray(category.subcategories)) {
      category.subcategories = Object.values(category.subcategories)
    }

    // Concatenate the children to the parent
    category.subcategories = [
      ...category.subcategories,
      ...result.filter(child => child.parent === category.term_id)
    ]

    // Filter out empty subcategories
    category.subcategories = category.subcategories.filter(
      child => child.count > 0
    )

    return category
  })

  // Return the result
  return result
}

export default function PracticesWithCategory({
  cache,
  practiceKey,
  baseUrl,
  cacheKey,
  handlePracticeFetch,
  chatURI = ROUTE_CHAT_GENERAL
}) {
  const introVideoData = getPracticeDetails(practiceKey)

  const [activeFilterIndex, setActiveFilterIndex] = useState(0)
  const [categories, setCategories] = useState([])
  const [filteredPractices, setFilteredPractices] = useState([])

  const { data, isLoaded } = useFetchAllList(
    handlePracticeFetch,
    cache,
    cacheKey
  )

  const setActiveFilter = index => {
    // Return early if we don't have any courses
    if (!categories?.length) return

    setActiveFilterIndex(index)
  }

  // Re-render when the active filter changes
  useEffect(() => {
    // Return early if we don't have any practice data
    if (!data?.length) return

    // If we haven't updated the categories yet, do so now
    if (!categories?.length) {
      ;(async () => {
        // Get the categories
        const categoriesData = await _getPracticeCategoriesAsync(practiceKey)

        // Now that we have the categories and practices, we can filter the practices
        // But let's start by filtering the categories that don't have any practices
        let filteredCategories = categoriesData.filter(category =>
          data.some(practice =>
            practice.categories.some(cat => cat.id === category.term_id)
          )
        )

        // Next, filter out any categories that don't have any subcategories
        filteredCategories = filteredCategories.filter(
          category => category.subcategories.length > 0
        )

        // Prepend an 'All' category
        filteredCategories = [
          { name: 'All', id: -1, count: 0 },
          ...filteredCategories
        ]
        setCategories(filteredCategories)
      })()
    }

    const category = categories[activeFilterIndex]
    let filtered = data.filter(practice =>
      practice.categories.some(cat => cat.id === category?.term_id)
    )

    // Randomize the order of the practices
    filtered.sort(() => Math.random() - 0.5)

    setFilteredPractices(filtered)
  }, [activeFilterIndex, categories, data, practiceKey])

  return (
    <Loading isLoaded={!isLoaded}>
      <Container className='my-4 practice-categories'>
        <PracticeIntroVideo data={introVideoData} baseUrl={baseUrl} />
        <ChatCTA message="Get Mia's help" uri={chatURI} />

        {categories?.length > 0 && (
          <PracticeCategoryFilter
            categories={categories}
            activeFilterIndex={activeFilterIndex}
            setActiveFilter={setActiveFilter}
          />
        )}

        {/* Show filtered or all? */}
        {filteredPractices.length > 0 ? (
          <ScrollingCardsPracticeList
            practices={filteredPractices}
            baseUrl={baseUrl}
          />
        ) : (
          data?.length > 0 &&
          categories.map(category => {
            let practices = data?.filter(practice =>
              practice.categories.some(cat => cat.id === category.term_id)
            )

            // Return early if we don't have any practices
            if (!practices?.length) return null

            // Shuffle practices (to avoid showing the same ones first)
            practices = practices.sort(() => Math.random() - 0.5)

            return (
              <Col xs={12} key={category.term_id}>
                <h3 className='mb-0'>{category.name}</h3>
                <ScrollingCardsPracticeList
                  practices={practices}
                  baseUrl={baseUrl}
                />
              </Col>
            )
          })
        )}
      </Container>
    </Loading>
  )
}
